import { Fragment, useEffect, useRef, useState } from 'react';

import Button from 'components/Button';
import CategoryNavigation from 'components/CategoryNavigation';
import ErrorBoundary from 'components/ErrorBoundary';
import LoadMoreList from 'components/LoadMoreList';
import Meter from 'components/Meter';
import ProductCard from 'components/ProductCard';
import ScreenReaderAnnouncementText from 'components/ScreenReaderAnnouncementText';
import {
	PRODUCT_LIST_OFFSET_QUERY_PARAM,
	PRODUCT_LIST_PAGE_SIZE_QUERY_PARAM,
	PRODUCT_LIST_SORTING_QUERY_PARAM,
	SEARCH_QUERY_PARAM,
} from 'constants/queryParams';
import { useEffectOnce, usePagination, useValueChangeEffect } from 'hooks';
import {
	type GTMItemListId,
	type GTMItemListName,
	useProductListGTMEvents,
	usePromotionGTM,
} from 'hooks/gtm';
import { useSearchParams } from 'hooks/navigation';
import type { CategoryCardResponse, FacetResponse } from 'models/api';
import type { ProductCard as ProductCardModel } from 'models/productCard';
import type { SitecoreProductList } from 'models/sitecore';
import type { WithRequired } from 'types';
import { cn } from 'utils/classNames';
import { filterObject, filterTruthy } from 'utils/collection';
import { selectFocusable } from 'utils/dom';
import { empty, is } from 'utils/helpers';
import { useI18n } from 'utils/i18n';

import {
	mapSearchResultCategoriesMicroContent,
	refineMicroContent,
} from './helpers';
import ProductListActiveFilters from './ProductListActiveFilters';
import ProductListFiltersPopover, {
	SORT_FACET_ID,
} from './ProductListFiltersPopover';
import ProductListMicroContentItem from './ProductListMicroContentItem';

type ProductListFacetWithItems = WithRequired<FacetResponse, 'facetItems'>;

interface Props {
	categories?: CategoryCardResponse[];
	className?: string;
	fields: SitecoreProductList;
	gtmItemListId: GTMItemListId;
	gtmItemListName: GTMItemListName;
	placeholderComponentName: string;
	searchQuery?: string;
}

export const PRODUCT_LIST_GRID_CLASSES =
	'grid grid-cols-2 gap-x-r-md md:grid-cols-4 lg:grid-cols-6';

// Row separator with pseudo elements set on the first item in each
// row. Can't just set to the bottom of each item since the last row
// may not have a 'full column set' of items so the separator would
// then not reach edge to edge.
export const PRODUCT_LIST_ITEM_SEPARATOR_CLASSES = cn([
	'relative mb-16 md:mb-24',
	'after:absolute after:-bottom-8 after:left-0 after:w-[calc(100vw-(var(--spacing-r-md))*2)] after:max-w-col-12 after:border-b after:border-b-subtle after:content-none md:after:-bottom-12',
	'max-md:nth-[2n+1]:after:content-[""]',
	'md:max-lg:nth-[4n+1]:after:content-[""]',
	'lg:nth-[6n+1]:after:content-[""]',
]);

/**
 * Product list used for the full product catalog, search results and
 * smaller lists of selected products.
 */
export default function ProductList({
	categories,
	className,
	fields: initialFields,
	gtmItemListId,
	gtmItemListName,
	placeholderComponentName,
	searchQuery,
}: Props) {
	const { t, tEmpty, tPlural } = useI18n();
	const searchParams = useSearchParams();
	const filterContainerRef = useRef<HTMLDivElement>(null);
	const [isFilterPopoverOpen, setIsFilterPopoverOpen] = useState(false);
	const [selectedFilterPopoverFacetId, setSelectedFilterPopoverFacetId] =
		useState<string>('');

	const isFacetId = (key: string) =>
		initialFields.facets?.some((facet) => facet.id === key) ?? false;

	// Only include facets since sorting and search is handled below.
	const initialQueryVars = [...searchParams.entries()].filter(([key]) =>
		isFacetId(key),
	);
	const initialNonDefaultSort = initialFields.sortOptions?.find(
		(opt) =>
			!opt.default &&
			opt.attribute === searchParams.get(PRODUCT_LIST_SORTING_QUERY_PARAM),
	);
	if (initialNonDefaultSort) {
		initialQueryVars.push([
			PRODUCT_LIST_SORTING_QUERY_PARAM,
			initialNonDefaultSort.attribute,
		]);
	}
	if (searchQuery) {
		initialQueryVars.push([SEARCH_QUERY_PARAM, searchQuery]);
	}

	const refinedMicroContent =
		initialFields.microContent?.map(refineMicroContent) ?? empty.array;
	const microContent =
		[
			mapSearchResultCategoriesMicroContent(
				categories,
				tEmpty('micro_content_category_chip_links_title'),
			),
			...refinedMicroContent,
		].filter(Boolean) ?? empty.array;

	const firstMicroContentIndex = 5;
	// Each key is the product index where an item will be included after.
	const microContentItems = filterObject(
		{
			[firstMicroContentIndex]: microContent[0],
			12: microContent[1],
			19: microContent[2],
		},
		(item, key) =>
			Boolean(item) &&
			// Remove items that won't be displayed to make the view_promotion GTM
			// event correct. The first item will always be visible so use its index
			// as the minimum.
			Number.parseInt(key, 10) <=
				Math.max(initialFields.products.length, firstMicroContentIndex),
	);
	const microContentCount = Object.values(microContentItems).length;
	const { sendViewPromotionEvent } = usePromotionGTM();
	useEffectOnce(() => {
		// Count any rendered micro content as viewed. Only run once on mount to not
		// send events every time the list re-renders from stuff like filtering.
		Object.values(microContentItems).forEach((item) => {
			sendViewPromotionEvent(item);
		});
	});

	const {
		component: productListFields,
		getQueryVarValue,
		hasQueryVar,
		isLoading: isLoadingProducts,
		items: products,
		loadMore: loadMoreProducts,
		queryVarItems,
		updateQueryVars,
	} = usePagination<ProductCardModel, SitecoreProductList>({
		defaultPageSize: initialFields.initialPageSize,
		initialComponent: initialFields,
		initialItems: initialFields.products,
		initialNextPageOffset: initialFields.nextPageOffset,
		initialQueryVars,
		firstPageItemCountOffset: -microContentCount,
		itemsKey: 'products',
		offsetQueryVarName: PRODUCT_LIST_OFFSET_QUERY_PARAM,
		pageSizeQueryVarName: PRODUCT_LIST_PAGE_SIZE_QUERY_PARAM,
		placeholderComponentName,
	});

	const facets = (productListFields?.facets ?? empty.array).filter(
		(facet): facet is ProductListFacetWithItems =>
			is.arrayWithLength(facet.facetItems),
	);
	const facetCount = facets.length;
	const hasFacets = facetCount > 0;
	const maxVisibleFacetButtons = 6;
	const hasHiddenFacets = facetCount > maxVisibleFacetButtons;
	// Count the 'view all' button as part of the limit, so if it needs to be
	// visible, hide an additional facet to keep the count limit.
	const visibleFacets = hasHiddenFacets
		? facets.slice(0, maxVisibleFacetButtons - 1)
		: facets;

	const sortOptions = productListFields?.sortOptions ?? empty.array;
	const hasSortOptions = sortOptions.length > 0;

	const hasNextPage = Boolean(productListFields?.hasNextPage);
	const totalProductsCount = productListFields?.total ?? 0;
	const visibleProductsCount = products.length;

	const getSortOption = (value: string | undefined) =>
		sortOptions.find((opt) => opt.attribute === value);

	const clearAllFacets = () => {
		updateQueryVars((vars) => {
			facets.forEach((facet) => {
				vars.delete(facet.id);
			});
		});
	};

	const getMicroContentData = (productIndex: number) => {
		const isLastProduct = productIndex === visibleProductsCount - 1;
		// If on the last product and the total number of products are
		// below the first micro content index, force that index to
		// always show at least one micro content.
		const key =
			isLastProduct && visibleProductsCount <= firstMicroContentIndex
				? firstMicroContentIndex
				: productIndex;
		return is.keyOf(microContentItems, key)
			? microContentItems[key]
			: undefined;
	};

	const { sendViewItemListEvent } = useProductListGTMEvents(
		gtmItemListId,
		gtmItemListName,
	);
	useEffect(() => {
		if (products.length > 0) {
			const offset = searchParams.get(PRODUCT_LIST_OFFSET_QUERY_PARAM);
			sendViewItemListEvent(
				products,
				offset ? Number.parseInt(offset, 10) : initialFields.nextPageOffset,
			);
		}
		// oxlint-disable-next-line react-hooks/exhaustive-deps
	}, [products]);

	// Reset filtering when searching for something new.
	useValueChangeEffect(searchQuery, () => {
		clearAllFacets();
	});

	const updateSorting = (value: string) => {
		const selected = getSortOption(value);
		updateQueryVars((vars) => {
			if (selected?.default) {
				vars.delete(PRODUCT_LIST_SORTING_QUERY_PARAM);
			} else {
				vars.set(PRODUCT_LIST_SORTING_QUERY_PARAM, value);
			}
		});
	};

	const updateFacet = (name: string, value: string, checked: boolean) => {
		updateQueryVars((vars) => {
			if (checked) {
				vars.add([name, value]);
			} else {
				vars.delete(name, value);
			}
		});
	};

	const clearFacet = (name: string, value: string) => {
		updateQueryVars((vars) => {
			vars.delete(name, value);
		});
	};

	// Facets that have active items, and only with those active items.
	const activeFacets = facets
		.map((facet): ProductListFacetWithItems => ({
			...facet,
			facetItems: facet.facetItems.filter((item) =>
				hasQueryVar(facet.id, item.item),
			),
		}))
		.filter((facet) => facet.facetItems.length > 0);

	const selectedSort = getSortOption(
		getQueryVarValue(PRODUCT_LIST_SORTING_QUERY_PARAM),
	);
	const defaultSort = sortOptions.find((opt) => opt.default);
	const selectedFacetValues = queryVarItems.filter(([key]) => isFacetId(key));

	const subcategories = filterTruthy(
		productListFields?.subcategories?.map((cat) => ({
			// Keep selected sort option when navigating between categories.
			href:
				selectedSort !== defaultSort && selectedSort?.attribute
					? `${cat.url}?${PRODUCT_LIST_SORTING_QUERY_PARAM}=${selectedSort.attribute}`
					: cat.url,
			imageSrc: cat.fields.image?.src,
			text: cat.fields.name,
		})),
		'href',
		'text',
	);
	const hasSubcategories = is.arrayWithLength(subcategories);

	return (
		<div className={className}>
			<ErrorBoundary isPageWidth>
				{hasSubcategories && (
					<CategoryNavigation
						containerTag="nav"
						aria-label={t('product_list_subcategories_label')}
						className={className}
						items={subcategories}
					/>
				)}

				{hasFacets && (
					<div
						ref={filterContainerRef}
						className={cn(
							'flex items-center border-b border-b-subtle pb-6',
							hasSubcategories && 'mt-r-xl',
						)}
					>
						<Button
							variant="branded"
							leadingIcon="tune"
							className="mr-4 md:hidden"
							aria-haspopup="dialog"
							onClick={() => {
								setSelectedFilterPopoverFacetId('');
								setIsFilterPopoverOpen(true);
							}}
						>
							{t('product_list_show_filters_mobile_button')}
						</Button>
						<div className="mr-4 flex flex-wrap gap-2 max-md:hidden">
							{visibleFacets.map((facet) => (
								<Button
									key={facet.id}
									onClick={() => {
										setSelectedFilterPopoverFacetId(facet.id);
										setIsFilterPopoverOpen(true);
									}}
									aria-haspopup="dialog"
								>
									{facet.title}
								</Button>
							))}
							{hasHiddenFacets && (
								<Button
									leadingIcon="tune"
									onClick={() => {
										setSelectedFilterPopoverFacetId('');
										setIsFilterPopoverOpen(true);
									}}
								>
									{t('product_list_all_filters_button')}
								</Button>
							)}
						</div>

						<ScreenReaderAnnouncementText
							as="p"
							text={tPlural('general_amount_of_products', totalProductsCount)}
							atomic
							className="mr-4 ml-auto self-center whitespace-nowrap"
						/>
						{hasSortOptions && (
							<Button
								onClick={() => {
									setSelectedFilterPopoverFacetId(SORT_FACET_ID);
									setIsFilterPopoverOpen(true);
								}}
								className="max-md:hidden"
								leadingIcon="arrows-compare"
								leadingIconClass="rotate-90"
								aria-haspopup="dialog"
							>
								{t('product_list_sorting_facet_heading')}
							</Button>
						)}

						<ProductListFiltersPopover
							disabled={isLoadingProducts}
							facets={facets}
							isOpen={isFilterPopoverOpen}
							onClose={() => {
								setIsFilterPopoverOpen(false);
							}}
							onFacetSelect={(facetId) => {
								setSelectedFilterPopoverFacetId(facetId);
							}}
							onFacetChange={(e) => {
								const input = e.currentTarget;
								updateFacet(input.name, input.value, input.checked);
							}}
							onSortChange={(e) => {
								updateSorting(e.currentTarget.value);
							}}
							selectedFacetId={selectedFilterPopoverFacetId}
							selectedFacetValues={selectedFacetValues}
							selectedSortOptionValue={
								getQueryVarValue(PRODUCT_LIST_SORTING_QUERY_PARAM) ||
								defaultSort?.attribute ||
								sortOptions[0]?.attribute ||
								''
							}
							sortOptions={sortOptions}
							totalProductsCount={totalProductsCount}
						/>
					</div>
				)}

				<ProductListActiveFilters
					activeFacets={activeFacets}
					className="my-6"
					disabled={isLoadingProducts}
					onClearAllClick={clearAllFacets}
					onClearItemClick={clearFacet}
					onFocusLoss={() => {
						if (filterContainerRef.current) {
							selectFocusable(filterContainerRef.current).at(-1)?.focus();
						}
					}}
				/>
			</ErrorBoundary>

			<LoadMoreList
				isLoading={isLoadingProducts}
				onLoadMoreClick={loadMoreProducts}
				hasLoadMoreButton={hasNextPage}
				loadedItemFocusTarget="first-interactive"
				buttonAlignment="center"
				buttonClassName="mt-4 max-sm:w-full sm:min-w-72"
				buttonText={t('load_more_products_button')}
				className="mt-r-xl"
				listClassName={cn(
					PRODUCT_LIST_GRID_CLASSES,
					'relative transition-opacity',
					isLoadingProducts && 'opacity-50',
				)}
				afterListContent={
					<Meter
						alignment="center"
						current={visibleProductsCount}
						max={totalProductsCount}
						labelHasProgress
						label={t('product_list_page_indicator_text', {
							numShown: visibleProductsCount,
							numTotal: totalProductsCount,
						})}
					/>
				}
			>
				{products.map((product, i) => {
					const microContentData = getMicroContentData(i);

					return (
						<Fragment
							key={`${product.productId}-${product.id}-${product.title}`}
						>
							<ProductCard
								product={product}
								productListIndex={i}
								gtmItemListId={gtmItemListId}
								gtmItemListName={gtmItemListName}
								className={PRODUCT_LIST_ITEM_SEPARATOR_CLASSES}
								isColumnEvenHeight
							/>
							{microContentData && (
								<div
									className={cn('flex', PRODUCT_LIST_ITEM_SEPARATOR_CLASSES)}
								>
									<ProductListMicroContentItem data={microContentData} />
								</div>
							)}
						</Fragment>
					);
				})}
			</LoadMoreList>
		</div>
	);
}
ProductList.displayName = 'ProductList';
